import type { NavigateOptions } from '@tanstack/react-router';

import { TLResourceType } from '@/api/gen/tessera-backend-api.schemas';

/** Ancestor ids keyed by the name they travel under, e.g. `{ workflow_id, remedy_id }`. */
export type NotificationAncestorIds = Readonly<Record<string, string | undefined>>;

export type NotificationNavIntent = {
  resourceType: string | null | undefined;
  resourceId?: string | null;
  /**
   * Ancestors looked up by name rather than position, so a type that nests deeper adds a
   * key instead of shifting the others. `parentResourceId` remains the fallback for links
   * minted before the named params existed.
   */
  ancestorIds?: NotificationAncestorIds;
  parentResourceId?: string | null;
  navigationTab?: string | null;
  filename?: string | null;
};

/**
 * A backend `deep_link` is only honoured when it is this tenant's own `/go` path.
 *
 * The value arrives from the API and is handed straight to the router, so without this an
 * absolute URL would be an open redirect and another tenant's path would be a cross-tenant
 * navigation. Anything else returns null and the caller rebuilds the route locally.
 */
export function safeNotificationDeepLink(deepLink: string | null | undefined, tenantId: string): string | null {
  // Protocol-relative (`//host`) is same-scheme but off-origin, so it must go with the rest.
  if (!deepLink || !deepLink.startsWith('/') || deepLink.startsWith('//')) return null;

  const segments = deepLink.split('?')[0].split('/');
  if (segments.length !== 4 || segments[1] !== 't' || segments[3] !== 'go') return null;

  try {
    return decodeURIComponent(segments[2]) === tenantId ? deepLink : null;
  } catch {
    return null; // malformed percent-encoding in the tenant segment
  }
}

export function resolveNotificationRoute(intent: NotificationNavIntent, tenantId: string): NavigateOptions | null {
  const { resourceType, resourceId, ancestorIds, navigationTab, filename } = intent;
  if (!resourceType) return null;

  const ancestor = (name: string) => ancestorIds?.[name] || intent.parentResourceId;

  switch (resourceType) {
    case TLResourceType.WF_WORKFLOW: {
      const workflowId = resourceId || ancestor('workflow_id');
      if (!workflowId) return { to: '/t/$tenantId/loom', params: { tenantId }, search: { owner: 'shared' } };
      return { to: '/t/$tenantId/loom/$workflowId', params: { tenantId, workflowId } };
    }
    case TLResourceType.WF_WORKFLOW_TEMPLATE: {
      const templateId = resourceId || ancestor('template_id');
      if (!templateId) return null;
      return { to: '/t/$tenantId/loom/templates/$templateId', params: { tenantId, templateId } };
    }
    case TLResourceType.WF_WORKFLOW_EXECUTION: {
      const workflowId = ancestor('workflow_id');
      if (!workflowId || !resourceId) return null;
      return {
        to: '/t/$tenantId/loom/$workflowId',
        params: { tenantId, workflowId },
        search: { execId: resourceId },
      };
    }
    case TLResourceType.DH_DASHBOARD:
      if (!resourceId) return null;
      return {
        to: '/t/$tenantId/data-harmonization/dashboard/$dashboardId',
        params: { tenantId, dashboardId: resourceId },
      };
    case TLResourceType.CR_SCAN:
      if (!resourceId) return null;
      return { to: '/t/$tenantId/code-modernization/$remedyId', params: { tenantId, remedyId: resourceId } };
    case TLResourceType.KNOWLEDGE_BASE_PACKAGE:
      if (!resourceId) return null;
      return { to: '/t/$tenantId/knowledge-base/package/$packageId', params: { tenantId, packageId: resourceId } };
    case TLResourceType.MESSAGE: {
      const conversationId = ancestor('conversation_id');
      if (!conversationId) return null;
      return {
        to: '/t/$tenantId/conversation/$conversationId',
        params: { tenantId, conversationId },
      };
    }
    case TLResourceType.VR_HANA_VIEW:
      if (resourceId) {
        return { to: '/t/$tenantId/view-remediation/$viewId', params: { tenantId, viewId: resourceId } };
      }
      return {
        to: '/t/$tenantId/view-remediation',
        params: { tenantId },
        search: { tab: navigationTab === 'all' || navigationTab === 'assigned-to-me' ? navigationTab : undefined },
      };
    case TLResourceType.VR_VIEW_REVIEW:
    case TLResourceType.VR_VIEW_ASSIGNMENT: {
      const viewId = ancestor('view_id');
      if (!viewId) return null;
      return { to: '/t/$tenantId/view-remediation/$viewId', params: { tenantId, viewId } };
    }
    case TLResourceType.CR_REMEDY_OBJECT: {
      const remedyId = ancestor('remedy_id');
      if (!remedyId) return null;
      if (resourceId) {
        return {
          to: '/t/$tenantId/code-modernization/$remedyId/$objectId',
          params: { tenantId, remedyId, objectId: resourceId },
        };
      }
      return { to: '/t/$tenantId/code-modernization/$remedyId', params: { tenantId, remedyId } };
    }
    case TLResourceType.PROGRAM:
      if (!resourceId) return null;
      return {
        to: '/t/$tenantId',
        params: { tenantId },
        search: { previewProgramId: resourceId, previewFilename: filename || undefined },
      };
    default:
      return null;
  }
}
